import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Loader, Layers } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../api/axios';
import { API_ENDPOINTS } from '../../api/endpoints';
import { ProcessGenerateFlowchartModal } from '../processes/ProcessGenerateFlowchartModal';
import { useProcessById } from '../../queries/processes/useProcessById';
import { processesQueryKey } from '../../queries/sox';
import {
  buildProcessReturnState,
  currentPathWithSearch,
  mergeProcessNavigationState,
  soxProcessFlowchartPath,
  soxCycleCombinedFlowchartPath,
} from '../../routing/processNavigation.ts';
import { TENANT_PATHS } from '../../routing/paths.ts';
import { useProcessBackNavigation } from '../../hooks/useProcessBackNavigation.ts';
import { getApiErrorMessage, resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import CycleEndToEndProcessChart from './CycleEndToEndProcessChart';
import EndToEndSubprocessLinkModal from './EndToEndSubprocessLinkModal';
import EndToEndConnectorLinksModal from './EndToEndConnectorLinksModal';
import { fetchAllCycleProcesses, sortProcessesForEndToEnd } from './cycleEndToEndProcesses';
import { EMPTY_END_TO_END_MAP, fetchCycleEndToEndMap, saveCycleEndToEndMap } from './endToEndMapApi';

export default function CycleEndToEndFlowchartPage() {
  const { cycleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { goBack } = useProcessBackNavigation(TENANT_PATHS.SOX_DASHBOARD);
  const [missingFlowchartProcess, setMissingFlowchartProcess] = useState(null);
  const [generateFlowchartTarget, setGenerateFlowchartTarget] = useState(null);
  const [subprocessLinkModal, setSubprocessLinkModal] = useState(null);
  const [connectorLinksModal, setConnectorLinksModal] = useState(null);

  const {
    data: processes = [],
    isLoading: isLoadingProcesses,
    isError: isProcessesError,
  } = useQuery({
    queryKey: ['cycle-end-to-end-flowchart', cycleId],
    enabled: Boolean(cycleId),
    queryFn: () => fetchAllCycleProcesses(axiosInstance, API_ENDPOINTS.getProcesses, cycleId),
  });

  const {
    data: savedMap = EMPTY_END_TO_END_MAP,
    isLoading: isLoadingMap,
    isError: isMapError,
  } = useQuery({
    queryKey: ['cycle-end-to-end-map', cycleId],
    enabled: Boolean(cycleId),
    queryFn: () => fetchCycleEndToEndMap(cycleId),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const mapStateRef = useRef(EMPTY_END_TO_END_MAP);
  const saveTilesTimerRef = useRef(null);
  const saveMapMutateRef = useRef(null);
  const handleMapChange = (nextMap) => {
    mapStateRef.current = nextMap;
  };

  const saveMapMutation = useMutation({
    mutationFn: () => saveCycleEndToEndMap(cycleId, mapStateRef.current),
    onError: (error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to save end-to-end map')));
    },
  });
  saveMapMutateRef.current = saveMapMutation.mutate;

  useEffect(() => {
    return () => {
      if (saveTilesTimerRef.current) {
        window.clearTimeout(saveTilesTimerRef.current);
      }
    };
  }, []);

  const handleTilesMoved = useCallback(() => {
    if (!cycleId) return;
    if (saveTilesTimerRef.current) {
      window.clearTimeout(saveTilesTimerRef.current);
    }
    saveTilesTimerRef.current = window.setTimeout(() => {
      saveMapMutateRef.current?.();
    }, 400);
  }, [cycleId]);

  const orderedProcesses = useMemo(() => sortProcessesForEndToEnd(processes), [processes]);
  const hasAnyFlowchart = useMemo(
    () => orderedProcesses.some((process) => Boolean(process?.has_flowchart)),
    [orderedProcesses]
  );
  const isLoading = isLoadingProcesses || isLoadingMap;
  const isError = isProcessesError || isMapError;

  const generateProcessListKey = generateFlowchartTarget?.processListKey;
  const {
    data: processDetailsForGenerate,
    isFetching: isFetchingProcessDetailsForGenerate,
  } = useProcessById(generateProcessListKey, {
    enabled: Boolean(generateFlowchartTarget),
    placeholderProcess: generateFlowchartTarget?.process ?? null,
  });

  const openGenerateFlowchartModal = (process) => {
    const processListKey = String(process?.process_id ?? process?.id ?? '').trim();
    if (!processListKey) return;
    setGenerateFlowchartTarget({ process, processListKey });
  };

  const handleProcessClick = (process) => {
    const processListKey = String(process?.process_id ?? process?.id ?? '').trim();
    if (!processListKey) return;

    if (process?.has_flowchart) {
      navigate(soxProcessFlowchartPath(cycleId, processListKey), {
        state: buildProcessReturnState(currentPathWithSearch(location)),
      });
      return;
    }

    setMissingFlowchartProcess(process);
  };

  const handleFlowchartGenerated = async (versionNumber) => {
    const target = generateFlowchartTarget;
    if (!target) return;
    await queryClient.invalidateQueries({ queryKey: ['cycle-end-to-end-flowchart', cycleId] });
    await queryClient.invalidateQueries({ queryKey: processesQueryKey(cycleId) });
    setGenerateFlowchartTarget(null);
    setMissingFlowchartProcess(null);
    navigate(soxProcessFlowchartPath(cycleId, target.processListKey), {
      state: mergeProcessNavigationState(
        {
          syncInitialPositions: true,
          ...(versionNumber != null ? { versionNumber } : {}),
        },
        currentPathWithSearch(location)
      ),
    });
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#eef3f8] overflow-hidden">
      <div className="sticky top-0 z-10 border-b border-slate-200/80 bg-white/90 px-4 py-4 backdrop-blur sm:px-6 lg:px-8">
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <button
              type="button"
              onClick={goBack}
              className="rounded-full p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              aria-label="Back"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-slate-900">End to end flowchart</h1>
              <p className="text-sm text-slate-500">
                Cycle {String(cycleId || '').toUpperCase()} · Drag to move · Pull a dot to connect · Click a process to open it
              </p>
              <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5">
                  <span className="h-2 w-2 rounded-full bg-sky-600 ring-2 ring-white" />
                  <span>Right = output</span>
                  <span className="font-medium text-sky-700">——→</span>
                </span>
                <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5">
                  <span className="h-2 w-2 rounded-full bg-sky-600 ring-2 ring-white" />
                  <span>Left = input</span>
                  <span className="font-medium text-sky-700">←——</span>
                </span>
                <span className="text-slate-400">Connect right to left</span>
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() =>
                navigate(soxCycleCombinedFlowchartPath(cycleId), {
                  state: buildProcessReturnState(currentPathWithSearch(location)),
                })
              }
              disabled={isLoading || isError || !hasAnyFlowchart}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Layers className="h-3.5 w-3.5" />
              See combine flowchart
            </button>
          </div>
        </div>
      </div>

      <div className="relative h-[calc(100vh-73px)] min-h-[520px] w-full">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader className="h-8 w-8 animate-spin text-slate-800" />
          </div>
        ) : isError ? (
          <div className="mx-auto max-w-xl px-4 py-8">
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              Failed to load the end-to-end map for this cycle.
            </div>
          </div>
        ) : (
          <CycleEndToEndProcessChart
            processes={orderedProcesses}
            savedMap={savedMap}
            onMapChange={handleMapChange}
            onProcessClick={handleProcessClick}
            onConnectProcesses={({ sourceProcess, targetProcess }) =>
              setSubprocessLinkModal({ sourceProcess, targetProcess })
            }
            onEditConnection={({ sourceProcess, targetProcess }) =>
              setConnectorLinksModal({ sourceProcess, targetProcess })
            }
            onTilesMoved={handleTilesMoved}
          />
        )}
      </div>

      <EndToEndSubprocessLinkModal
        isOpen={Boolean(subprocessLinkModal)}
        cycleId={cycleId}
        sourceProcess={subprocessLinkModal?.sourceProcess}
        targetProcess={subprocessLinkModal?.targetProcess}
        onClose={() => setSubprocessLinkModal(null)}
        onChanged={() => {
          queryClient.invalidateQueries({ queryKey: ['cycle-end-to-end-map', cycleId] });
        }}
      />

      <EndToEndConnectorLinksModal
        isOpen={Boolean(connectorLinksModal)}
        cycleId={cycleId}
        sourceProcess={connectorLinksModal?.sourceProcess}
        targetProcess={connectorLinksModal?.targetProcess}
        onClose={() => setConnectorLinksModal(null)}
        onChanged={() => {
          queryClient.invalidateQueries({ queryKey: ['cycle-end-to-end-map', cycleId] });
        }}
        onConnectMore={({ sourceProcess, targetProcess }) => {
          setConnectorLinksModal(null);
          setSubprocessLinkModal({ sourceProcess, targetProcess });
        }}
      />

      {missingFlowchartProcess ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-gray-900">No flowchart yet</h2>
            <p className="mt-2 text-sm text-gray-600">
              Currently there is no flowchart for{' '}
              <span className="font-medium text-gray-900">
                {missingFlowchartProcess.name || 'this process'}
              </span>
              . Generate one if you want to open it.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => setMissingFlowchartProcess(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                onClick={() => {
                  openGenerateFlowchartModal(missingFlowchartProcess);
                }}
              >
                Generate Flowchart
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ProcessGenerateFlowchartModal
        isOpen={Boolean(generateFlowchartTarget)}
        onClose={() => setGenerateFlowchartTarget(null)}
        cycleId={cycleId}
        processId={generateFlowchartTarget?.processListKey}
        processName={generateFlowchartTarget?.process?.name}
        description={generateFlowchartTarget?.process?.description || ''}
        initialProcess={processDetailsForGenerate ?? generateFlowchartTarget?.process}
        isLoading={Boolean(generateFlowchartTarget) && isFetchingProcessDetailsForGenerate}
        onGenerated={handleFlowchartGenerated}
      />
    </div>
  );
}
